import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { purchaseActions } from '../../action/purchaseActions';


const PurchaseViewModal = ({ purchase, onClose }) => {
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Format date with time
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(num);
    };

    // Get status badge class
    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return 'badge bg-warning';
            case 'approved': return 'badge bg-info';
            case 'received': return 'badge bg-success';
            case 'returned': return 'badge bg-info';
            case 'canceled': return 'badge bg-danger';
            case 'completed': return 'badge bg-success';
            default: return 'badge bg-secondary';
        }
    };

    // Get payment status badge class
    const getPaymentStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'unpaid': return 'badge bg-danger';
            case 'partial': return 'badge bg-warning';
            case 'paid': return 'badge bg-success';
            default: return 'badge bg-secondary';
        }
    };

    useEffect(() => {
        if (purchase?.id && purchase.payment_count > 0) {
            fetchPaymentHistory();
        }
    }, [purchase?.id]);

    const fetchPaymentHistory = async () => {
        try {
            setLoading(true);
            const result = await purchaseActions.getPaymentHistory(purchase.id, 1, 5);
            if (result.success) {
                setPaymentHistory(result.data.payments || []);
            }
        } catch (error) {
            console.error('Error fetching payment history:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!purchase) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} id="purchase-view-modal">
                <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Purchase Details: {purchase.reference_no}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                {/* Basic Information */}
                                <div className="col-md-6">
                                    <div className="card mb-3">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0">Basic Information</h6>
                                        </div>
                                        <div className="card-body">
                                            <table className="table table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td width="40%"><strong>Supplier:</strong></td>
                                                        <td>{purchase.supplier_name || 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Purchase Date:</strong></td>
                                                        <td>{formatDate(purchase.purchase_date)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Reference No:</strong></td>
                                                        <td>{purchase.reference_no}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Status:</strong></td>
                                                        <td>
                                                            <span className={getStatusBadge(purchase.status)}>
                                                                {purchase.status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Payment Status:</strong></td>
                                                        <td>
                                                            <span className={getPaymentStatusBadge(purchase.payment_status)}>
                                                                {purchase.payment_status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Created By:</strong></td>
                                                        <td>{purchase.created_by || 'Admin'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Amount Summary */}
                                <div className="col-md-6">
                                    <div className="card mb-3">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0">Amount Summary</h6>
                                        </div>
                                        <div className="card-body">
                                            <table className="table table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td width="50%"><strong>Subtotal:</strong></td>
                                                        <td className="text-end">{formatCurrency(purchase.subtotal)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Tax:</strong></td>
                                                        <td className="text-end">{formatCurrency(purchase.tax)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Discount:</strong></td>
                                                        <td className="text-end">{formatCurrency(purchase.discount)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Shipping:</strong></td>
                                                        <td className="text-end">{formatCurrency(purchase.shipping)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Grand Total:</strong></td>
                                                        <td className="text-end fw-bold text-primary">{formatCurrency(purchase.grand_total)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Paid Amount:</strong></td>
                                                        <td className="text-end text-success">{formatCurrency(purchase.paid_amount)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Due Amount:</strong></td>
                                                        <td className={`text-end fw-bold ${purchase.due_amount > 0 ? 'text-danger' : 'text-success'}`}>
                                                            {formatCurrency(purchase.due_amount)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Items List */}
                                {purchase.items && purchase.items.length > 0 && (
                                    <div className="col-12">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Items</h6>
                                            </div>
                                            <div className="card-body p-0">
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-hover mb-0">
                                                        <thead className="bg-light">
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Product</th>
                                                                <th>SKU</th>
                                                                <th className="text-end">Qty</th>
                                                                <th className="text-end">Unit Price</th>
                                                                <th className="text-end">Discount</th>
                                                                <th className="text-end">Tax</th>
                                                                <th className="text-end">Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {purchase.items.map((item, index) => (
                                                                <tr key={item.id || index}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{item.product_name}</td>
                                                                    <td>{item.sku || '-'}</td>
                                                                    <td className="text-end">{item.quantity}</td>
                                                                    <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                                    <td className="text-end">{formatCurrency(item.discount || 0)}</td>
                                                                    <td className="text-end">{formatCurrency(item.tax_amount || 0)}</td>
                                                                    <td className="text-end fw-bold">{formatCurrency(item.total_cost)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Payment History Section */}
                                {purchase.payment_count > 0 && (
                                    <div className="col-12">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">
                                                    Payment History ({purchase.payment_count} total)
                                                </h6>
                                            </div>
                                            <div className="card-body p-0">
                                                {loading ? (
                                                    <div className="text-center p-3">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </div>
                                                ) : paymentHistory.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-hover mb-0">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th className="text-end">Amount</th>
                                                                    <th>Method</th>
                                                                    <th>Reference</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {paymentHistory.map((payment, index) => (
                                                                    <tr key={payment.id || index}>
                                                                        <td>{formatDate(payment.date)}</td>
                                                                        <td className="text-end text-success fw-bold">{formatCurrency(payment.amount)}</td>
                                                                        <td>
                                                                            <span className={`badge ${
                                                                                payment.method === 'cash' ? 'bg-success' :
                                                                                payment.method === 'bank_transfer' ? 'bg-info' :
                                                                                payment.method === 'cheque' ? 'bg-warning' :
                                                                                payment.method === 'credit_card' ? 'bg-primary' : 'bg-secondary'
                                                                            }`}>
                                                                                {payment.method}
                                                                            </span>
                                                                        </td>
                                                                        <td>{payment.reference || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="text-center p-3 text-muted">
                                                        No payment records found
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notes */}
                                {purchase.notes && (
                                    <div className="col-12">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Notes</h6>
                                            </div>
                                            <div className="card-body">
                                                <p className="mb-0">{purchase.notes}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Timestamps */}
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0">Timestamps</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <small className="text-muted d-block">Created:</small>
                                                    <strong>{formatDateTime(purchase.created_at)}</strong>
                                                </div>
                                                <div className="col-md-6">
                                                    <small className="text-muted d-block">Last Updated:</small>
                                                    <strong>{formatDateTime(purchase.updated_at)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

PurchaseViewModal.propTypes = {
    purchase: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

export default PurchaseViewModal;